import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Drawer, Row, Space, Form, Input } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import NumberInput from 'app/components/NumberInput/NumberInput';
import CultureSelect from 'app/components/Select/CultureSelect/CultureSelect';
import UserSelect from 'app/components/Select/UserSelect/UserSelect';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { getRecipeColumns } from 'app/containers/Recipe/helpers';
import RecipeDetails from 'app/containers/Recipe/parts/RecipeDetails/RecipeDetails';
import { IRecipe, IRecipeFilters } from 'app/containers/Recipe/types';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ITableSort } from 'types';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import { useGetRecipeByIdQuery, useGetRecipeQuery } from '../../../../api';

interface IProps {
  isOpen: boolean;
  onRecipeSelect: (recipe: IRecipe) => void;
  onClose: () => void;
}

const TaskRecipeDrawer: FC<IProps> = ({ isOpen, onClose, onRecipeSelect }) => {
  const { t } = useTranslation();
  const { mobileOrTablet } = useResponsiveQuery();
  const [form] = Form.useForm();

  const envId = useSelector(selectSelectedEnvId) as number;
  const [recipeId, setRecipeId] = useState<number | null>(null);
  const [filters, setFilters] = useState<IRecipeFilters>({});

  const { currentData: recipe } = useGetRecipeByIdQuery(
    { envId, recipeId: recipeId as number },
    { skip: !recipeId },
  );
  const [sort, setSort] = useState<ITableSort>({});

  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const { data: recipeResponse, isFetching } = useGetRecipeQuery(
    {
      envId,
      pageSize,
      currentPage: currentPage - 1,
      filters,
      sort,
    },
    { skip: !envId },
  );

  useEffect(() => {
    form.setFieldsValue(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (recipeResponse?.totalPages && currentPage > recipeResponse.totalPages) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeResponse?.totalPages]);

  const columns: ColumnsType<IRecipe> = getRecipeColumns(t);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: unknown,
    sorter: SorterResult<IRecipe> | SorterResult<IRecipe>[],
  ) => {
    if ('field' in sorter && sorter.field && sorter.order) {
      const { field, order } = sorter;
      setSort({ field: field as string | string[], order });
    } else {
      setSort({});
    }

    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  return (
    <Drawer
      title={
        recipeId ? (
          <>
            <Button type="text" onClick={() => setRecipeId(null)}>
              <ArrowLeftOutlined />
            </Button>
            {recipe?.name}
          </>
        ) : (
          t('Recipe.Recipes', 'Recipes')
        )
      }
      visible={isOpen}
      forceRender={true}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={mobileOrTablet ? '100%' : 859}
      footer={
        <Row justify="end">
          <Button onClick={onClose}>{t('Common.Cancel', 'Cancel')}</Button>
        </Row>
      }
    >
      {recipeId ? (
        <Col>
          <RecipeDetails recipe={recipe} />
          <Button
            style={{ marginTop: 20 }}
            type="primary"
            onClick={() => onRecipeSelect(recipe as IRecipe)}
          >
            {t('Task.UseRecipe', 'Use recipe')}
          </Button>
        </Col>
      ) : (
        <Col>
          <Form
            form={form}
            requiredMark={true}
            id="recipeFiltersForm"
            onValuesChange={(_, values: IRecipeFilters) => setFilters(values)}
          >
            <Row
              justify="space-between"
              align="middle"
              style={{ width: '100%' }}
              wrap={false}
            >
              <Form.Item name="name">
                <Input
                  style={{ minWidth: 100 }}
                  prefix={<SearchOutlined />}
                  placeholder={t('TransportFilters.Search', 'Search')}
                />
              </Form.Item>

              <Space>
                <Form.Item name="cultureIds">
                  <CultureSelect mode="multiple" style={{ minWidth: 100 }} />
                </Form.Item>

                <Form.Item name="userId">
                  <UserSelect
                    style={{ minWidth: 100 }}
                    placeholder={t('Recipe.Creator', 'Creator')}
                  />
                </Form.Item>
              </Space>

              <Space>
                <Form.Item name="bbchFrom">
                  <NumberInput
                    style={{ width: 105 }}
                    placeholder={t('Recipe.BbchFrom', 'BBCH from')}
                  />
                </Form.Item>

                <Form.Item name="bbchTo">
                  <NumberInput
                    style={{ width: 105 }}
                    placeholder={t('Recipe.BbchTo', 'BBCH to')}
                  />
                </Form.Item>
              </Space>
            </Row>
          </Form>

          <StyledTable
            onRow={recipe => ({
              onClick: () => setRecipeId(recipe.id),
            })}
            rowKey="id"
            dataSource={recipeResponse?.content}
            columns={columns}
            loading={isFetching}
            pagination={{
              current: currentPage,
              total: recipeResponse?.totalElements,
              pageSize,
            }}
            onChange={onTableChange}
          />
        </Col>
      )}
    </Drawer>
  );
};

export default TaskRecipeDrawer;
